import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { rebuildFullOrder } from '../utils/reorderUtils';
import UserAvatar from './UserAvatar';
import './MilestoneView.css';

const PHASE_COLORS = ['#8b5cf6', '#3b82f6', '#16a34a', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6', '#6366f1'];

export default function MilestoneView({
  tasks,
  onToggleTask,
  onUpdateTaskMetadata,
  onReorderTasks,
  listId,
  onOpenTaskDrawer,
  isShared = false,
}) {
  const [dragState, setDragState] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  // Card currently hovered as a within-column drop target (insert-before)
  const [dropCardId, setDropCardId] = useState(null);
  const [optimisticOverrides, setOptimisticOverrides] = useState({});
  const [extraPhases, setExtraPhases] = useState(0);
  const prevTasksRef = useRef(tasks);

  // Clear optimistic overrides once the tasks prop updates with fresh data
  useEffect(() => {
    if (tasks !== prevTasksRef.current) {
      prevTasksRef.current = tasks;
      setOptimisticOverrides(prev => {
        if (Object.keys(prev).length === 0) return prev;
        const next = {};
        for (const [taskId, override] of Object.entries(prev)) {
          const task = tasks.find(t => t.id === taskId);
          if (!task) continue;
          const serverPhase = task.metadata?.milestone_phase || 1;
          if (serverPhase !== override.milestone_phase) {
            next[taskId] = override;
          }
        }
        return Object.keys(next).length === 0 ? {} : next;
      });
    }
  }, [tasks]);

  // Compute number of phases and columns
  const { columns, numPhases } = useMemo(() => {
    let maxPhase = 0;
    for (const task of tasks) {
      const override = optimisticOverrides[task.id];
      const phase = override
        ? override.milestone_phase
        : (task.metadata?.milestone_phase || 1);
      if (phase > maxPhase) maxPhase = phase;
    }

    const num = Math.max(3, maxPhase, 3 + extraPhases);
    const cols = {};
    for (let i = 1; i <= num; i++) {
      cols[i] = [];
    }

    for (const task of tasks) {
      const override = optimisticOverrides[task.id];
      const phase = override
        ? override.milestone_phase
        : (task.metadata?.milestone_phase || 1);
      const clampedPhase = Math.min(Math.max(1, phase), num);
      cols[clampedPhase].push(task);
    }

    return { columns: cols, numPhases: num };
  }, [tasks, optimisticOverrides, extraPhases]);

  const phaseColumns = useMemo(() => {
    const result = [];
    for (let i = 1; i <= numPhases; i++) {
      result.push({ id: i, label: `Phase ${i}` });
    }
    return result;
  }, [numPhases]);

  const handleDragStart = useCallback((e, taskId, sourceColumn) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
    setDragState({ taskId, sourceColumn });
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDragEnter = useCallback((columnId) => {
    setDropTarget(columnId);
  }, []);

  const handleDragLeave = useCallback((e, columnId) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTarget((prev) => (prev === columnId ? null : prev));
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragState(null);
    setDropTarget(null);
    setDropCardId(null);
  }, []);

  // Apply the phase change for a cross-phase move (optimistic + background API).
  const applyPhaseChange = useCallback(
    (taskId, targetPhase) => {
      setOptimisticOverrides((prev) => ({
        ...prev,
        [taskId]: { milestone_phase: targetPhase },
      }));
      onUpdateTaskMetadata(taskId, { milestone_phase: targetPhase })
        .catch(err => console.error('Error updating milestone phase:', err));
    },
    [onUpdateTaskMetadata]
  );

  // Drop on the phase column body: change phase only.
  const handleDrop = useCallback(
    (e, targetPhase) => {
      e.preventDefault();
      const taskId = e.dataTransfer.getData('text/plain');
      setDragState(null);
      setDropTarget(null);
      setDropCardId(null);

      if (!taskId || !dragState || dragState.sourceColumn === targetPhase) {
        return;
      }

      applyPhaseChange(taskId, targetPhase);
    },
    [dragState, applyPhaseChange]
  );

  // Drop onto a specific card: insert before it within the phase, and (for
  // cross-phase drops) also change phase. Persists the new global order.
  const handleCardDrop = useCallback(
    (e, targetTaskId, targetPhase) => {
      e.preventDefault();
      e.stopPropagation();
      const taskId = e.dataTransfer.getData('text/plain');
      const sourcePhase = dragState?.sourceColumn;
      setDragState(null);
      setDropTarget(null);
      setDropCardId(null);

      if (!taskId || !dragState || taskId === targetTaskId) {
        return;
      }

      const crossPhase = sourcePhase !== targetPhase;
      if (crossPhase) {
        applyPhaseChange(taskId, targetPhase);
      }

      if (!onReorderTasks) return;

      const ids = (columns[targetPhase] || []).map((t) => t.id);
      let newColumnOrder;
      let affectedIds;

      if (crossPhase) {
        let insertAt = ids.indexOf(targetTaskId);
        if (insertAt < 0) insertAt = ids.length;
        newColumnOrder = [...ids.slice(0, insertAt), taskId, ...ids.slice(insertAt)];
        affectedIds = new Set([...ids, taskId]);
      } else {
        const fromIndex = ids.indexOf(taskId);
        const toIndex = ids.indexOf(targetTaskId);
        if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
          return;
        }
        newColumnOrder = [...ids];
        const [moved] = newColumnOrder.splice(fromIndex, 1);
        newColumnOrder.splice(toIndex, 0, moved);
        affectedIds = new Set(ids);
      }

      onReorderTasks(listId, rebuildFullOrder(tasks, affectedIds, newColumnOrder));
    },
    [dragState, columns, tasks, listId, onReorderTasks, applyPhaseChange]
  );

  const handleCardDragOver = useCallback((e, taskId) => {
    e.preventDefault();
    e.stopPropagation();
    setDropCardId(taskId);
  }, []);

  const handleToggle = useCallback((e, taskId) => {
    e.stopPropagation();
    onToggleTask(listId, taskId).catch(err =>
      console.error('Error toggling task:', err)
    );
  }, [listId, onToggleTask]);

  return (
    <div className="milestone-container">
      {phaseColumns.map((col) => {
        const columnTasks = columns[col.id] || [];
        const isDropActive =
          dropTarget === col.id &&
          dragState &&
          dragState.sourceColumn !== col.id;
        const colorIndex = (col.id - 1) % PHASE_COLORS.length;

        return (
          <div
            key={col.id}
            className={`milestone-column${isDropActive ? ' milestone-column-drop-active' : ''}`}
            onDragOver={handleDragOver}
            onDragEnter={() => handleDragEnter(col.id)}
            onDragLeave={(e) => handleDragLeave(e, col.id)}
            onDrop={(e) => handleDrop(e, col.id)}
            style={{ '--phase-color': PHASE_COLORS[colorIndex] }}
          >
            <div className="milestone-column-header">
              <span>{col.label}</span>
              <span className="milestone-column-count">{columnTasks.length}</span>
            </div>
            <div className="milestone-column-body">
              {columnTasks.length === 0 ? (
                <div className="milestone-empty-column">No tasks</div>
              ) : (
                columnTasks.map((task) => (
                  <div
                    key={task.id}
                    className={`milestone-card${
                      dragState?.taskId === task.id ? ' milestone-card-dragging' : ''
                    }${task.completed ? ' milestone-card-completed' : ''}${
                      dragState && dropCardId === task.id && dragState.taskId !== task.id
                        ? ' milestone-card-drop-target'
                        : ''
                    }`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, task.id, col.id)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleCardDragOver(e, task.id)}
                    onDrop={(e) => handleCardDrop(e, task.id, col.id)}
                    onClick={() =>
                      onOpenTaskDrawer && onOpenTaskDrawer(task)
                    }
                  >
                    <div className="milestone-card-content">
                      <input
                        type="checkbox"
                        className="milestone-card-checkbox"
                        checked={task.completed}
                        onChange={(e) => handleToggle(e, task.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="milestone-card-text">
                        {task.text}
                        {task.hasNotes && (
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className="milestone-note-icon"
                            aria-label="Has notes"
                            title="This task has notes"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                            <polyline points="10 9 9 9 8 9" />
                          </svg>
                        )}
                      </div>
                      {isShared && task.assignedTo && task.assignedTo.name && (
                        <UserAvatar user={task.assignedTo} size="small" className="milestone-assignee-avatar" label="Assigned to" />
                      )}
                    </div>
                    {task.subTasksCount > 0 && (
                      <div className="milestone-card-meta">
                        <div className="milestone-subtask-progress">
                          <div className="milestone-subtask-bar">
                            <div
                              className="milestone-subtask-bar-fill"
                              style={{
                                width: `${
                                  (task.completedSubTasksCount /
                                    task.subTasksCount) *
                                  100
                                }%`,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
      <button
        className="milestone-add-phase-btn"
        onClick={() => setExtraPhases(prev => prev + 1)}
        title="Add phase"
        aria-label="Add phase"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" />
        </svg>
      </button>
    </div>
  );
}
